import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, AlertCircle, Mail, Loader2, ArrowLeft, Heart } from 'lucide-react'
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../../services/firebase'

const Unsubscribe = () => {
  const [params] = useSearchParams()
  const id = params.get('id')
  const [status, setStatus] = useState('idle') // idle | working | done | error | notfound | already
  const [email, setEmail] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!id) {
        setStatus('error')
        setErrorMsg('Missing subscriber ID in the link.')
        return
      }
      if (!db) {
        setStatus('error')
        setErrorMsg('Newsletter service is not available right now.')
        return
      }

      setStatus('working')
      try {
        const ref = doc(db, 'newsletter', id)
        const snap = await getDoc(ref)
        if (cancelled) return

        if (!snap.exists()) {
          setStatus('notfound')
          return
        }

        const data = snap.data()
        setEmail(data.email || '')

        if (data.active === false) {
          setStatus('already')
          return
        }

        await updateDoc(ref, {
          active: false,
          unsubscribedAt: serverTimestamp(),
        })
        if (!cancelled) setStatus('done')
      } catch (err) {
        console.error('Unsubscribe error:', err)
        if (!cancelled) {
          setStatus('error')
          setErrorMsg('Something went wrong. Please contact us at info@dlbcclarksville.org.')
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <section className="min-h-[70vh] flex items-center py-20 bg-gradient-to-b from-light-100 to-white">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          className="bg-white rounded-3xl shadow-soft border border-light-200 p-10 md:p-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {status === 'working' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-50 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-dark-100 mb-2">
                Updating your preferences…
              </h1>
              <p className="text-dark-300">One moment.</p>
            </>
          )}

          {status === 'done' && (
            <>
              <motion.div
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-50 flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
              >
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </motion.div>
              <h1 className="text-3xl font-heading font-bold text-dark-100 mb-3">
                You've been unsubscribed
              </h1>
              <p className="text-dark-300 mb-2">
                {email ? <><strong>{email}</strong> will no longer receive blog updates from us.</> : 'You will no longer receive blog updates from us.'}
              </p>
              <p className="text-dark-300 mb-8 text-sm">
                We're sorry to see you go. You're always welcome back.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </>
          )}

          {status === 'already' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <Mail className="w-10 h-10 text-gray-500" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-dark-100 mb-3">
                You're already unsubscribed
              </h1>
              <p className="text-dark-300 mb-8">
                {email ? <><strong>{email}</strong> is no longer on our list.</> : 'This email is no longer on our list.'}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </>
          )}

          {status === 'notfound' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-dark-100 mb-3">
                Subscription not found
              </h1>
              <p className="text-dark-300 mb-8">
                We couldn't find a subscription with this link. It may have already been removed.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-dark-100 mb-3">
                We hit a snag
              </h1>
              <p className="text-dark-300 mb-8">{errorMsg}</p>
              <a
                href="mailto:info@dlbcclarksville.org"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-colors"
              >
                <Heart className="w-4 h-4" />
                Contact Us
              </a>
            </>
          )}
        </motion.div>
      </div>
    </section>
  )
}

export default Unsubscribe
